import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaTruck, FaSyncAlt } from 'react-icons/fa';
import { fetchPublicTracking, buildDtdcTrackingLink } from '../../Services/courier';
import TrackingTimeline, {
  ShipmentProgress, STATUS_LABELS, formatScanTime,
} from '../AdminRoutes/Courier/TrackingTimeline';

/**
 * Patient facing tracking page. Reached through the link staff share, with no
 * login: the token in the URL is the only credential, and the API deliberately
 * returns nothing clinical.
 */
export default function TrackShipment() {
  const { trackingToken } = useParams();

  const [shipment, setShipment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true);
    try {
      const res = await fetchPublicTracking(trackingToken);
      setShipment(res?.data || null);
      setError(null);
    } catch (err) {
      setError(
        err?.response?.status === 404
          ? 'This tracking link is not valid any more.'
          : 'We could not load the tracking details. Please try again shortly.'
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [trackingToken]);

  useEffect(() => { load(); }, [load]);

  // Poll while the parcel is still moving, so a page left open stays current.
  useEffect(() => {
    if (!shipment || ['Delivered', 'Cancelled', 'Failed'].includes(shipment.status)) return undefined;
    const timer = setInterval(() => load({ silent: true }), 120000);
    return () => clearInterval(timer);
  }, [shipment, load]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-500">Loading your shipment…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <FaTruck className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-700">{error}</p>
          <p className="mt-1 text-xs text-slate-500">Please contact the clinic for help.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto w-full max-w-2xl space-y-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="flex items-center gap-2 text-lg font-bold tracking-tight text-slate-800">
                <FaTruck className="text-sky-600" /> Track your parcel
              </h1>
              <p className="mt-0.5 text-xs text-slate-500">
                {shipment?.consignmentNumber
                  ? `${shipment.provider} consignment ${shipment.consignmentNumber}`
                  : 'Your parcel is being prepared'}
              </p>
            </div>

            <button
              onClick={() => load({ silent: true })}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
            >
              <FaSyncAlt className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          <div className="mt-6">
            <ShipmentProgress status={shipment?.status} />
          </div>

          <div className="mt-6 grid grid-cols-2 gap-4 border-t border-slate-100 pt-5 text-sm">
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Status</p>
              <p className="font-semibold text-slate-800">
                {STATUS_LABELS[shipment?.status] || shipment?.status || '—'}
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Delivering to</p>
              <p className="font-medium text-slate-800">{shipment?.destinationCity || '—'}</p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">
                {shipment?.deliveredAt ? 'Delivered on' : 'Expected by'}
              </p>
              <p className="font-medium text-slate-800">
                {shipment?.deliveredAt
                  ? formatScanTime(shipment.deliveredAt)
                  : shipment?.expectedDeliveryDate
                  ? new Date(shipment.expectedDeliveryDate).toLocaleDateString('en-GB')
                  : 'To be confirmed'}
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500">Last updated</p>
              <p className="font-medium text-slate-800">
                {shipment?.lastTrackedAt ? formatScanTime(shipment.lastTrackedAt) : '—'}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-500">Journey</h2>
          <TrackingTimeline
            events={shipment?.events || []}
            emptyMessage="The courier has not scanned this parcel yet. Please check back soon."
          />
        </div>

        {shipment?.consignmentNumber && (
          <p className="text-center text-xs text-slate-500">
            You can also track this on the{' '}
            <a
              href={buildDtdcTrackingLink(shipment.consignmentNumber)}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-sky-600 hover:underline"
            >
              DTDC website
            </a>
            .
          </p>
        )}
      </div>
    </div>
  );
}
